import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import { SpanStyle } from '@/components/Typography/Typo';
import type { CHART_COLOR_SCALE } from '@/entities/chart/model/constants';
import { ChartColorScaleMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { radius } from '@/styles/sizes';
import { modalStyle } from '@/styles/style';
import { timeOut } from '@/utils/util';

type Props = {
  scale?: CHART_COLOR_SCALE;
  onDoneClick: (scale?: CHART_COLOR_SCALE) => void;
};

const ColorScaleModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const [scale, setScale] = useState<CHART_COLOR_SCALE | null>(
    props.scale || null,
  );

  return (
    <Style g={32}>
      <VFlex g={24}>
        <Heading type="h3">Color scale</Heading>
        <Flex w="wrap" g={16}>
          {ChartColorScaleMap.toArray().map((value) => {
            const colors = value.label;
            return (
              <ColorScaleItem
                key={value.value}
                colors={colors}
                selected={scale === value.value}
                onClick={() => setScale(value.value)}
              />
            );
          })}
        </Flex>
      </VFlex>

      <Flex a="center" j="space-between">
        <Button
          variant="outlined"
          sizeType="m"
          onClick={async () => {
            closeModal();
            await timeOut(200);
            props.onDoneClick(undefined);
          }}
        >
          Clear
        </Button>
        <Flex j="flex-end" g={8}>
          <Button variant="outlined" sizeType="m" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            sizeType="m"
            onClick={async () => {
              closeModal();
              await timeOut(200);
              props.onDoneClick(scale || undefined);
            }}
          >
            Done
          </Button>
        </Flex>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 454px;
  padding: 32px;
  ${modalStyle};
  .input-section {
    gap: 6px;
    align-items: center;
    .label {
      width: 40px;
      ${SpanStyle({
        type: 'label3',
        weight: 'm',
      })}
    }
  }
`;

type ColorScaleItemProps = {
  colors: string[];
  selected: boolean;
  onClick: () => void;
};

const ColorScaleItem = (props: ColorScaleItemProps) => {
  return (
    <ColorScaleItemStyle onClick={props.onClick} selected={props.selected}>
      <Flex className="color-scale-wrapper">
        {props.colors.map((scale, index) => {
          return (
            <Flex
              key={index}
              className="color-scale-chip"
              f={1}
              style={{ backgroundColor: scale }}
            />
          );
        })}
      </Flex>
    </ColorScaleItemStyle>
  );
};

type SelectedProps = {
  selected: boolean;
};

const ColorScaleItemStyle = styled(Flex)<SelectedProps>`
  cursor: pointer;
  padding: 8px;
  border-radius: ${radius.token.lg}px;
  border: 1px solid transparent;
  transition: all 0.2s;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
  }
  .color-scale-wrapper {
    border: 1px solid ${(props) => props.theme.colors.gray400};
    width: 100px;
    height: 40px;
  }
  ${({ selected }) =>
    selected &&
    css`
      border-color: ${(props) => props.theme.colors.primary100};
    `}
`;

export default ColorScaleModal;
